import { Assessment } from '@/common/types/assessment';
import { Activity } from '@/common/types/session';
import { ControllerType } from '@/common/types/student';
import getActivityContext from '@/common/utils/activityContext';
import { formatTimeTaken } from '@/common/utils/datetime';
import { mapActivityToSkill } from '@/common/utils/helpers';
import api from '@/db/apiClient';
import { HandGrabbingIcon, MouseLeftClickIcon } from '@phosphor-icons/react';
import { Badge } from '@shadcn/components/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import dayjs from 'dayjs';
import { KeyboardIcon } from 'lucide-react';
import { ReactNode, useEffect, useMemo, useState } from 'react';

interface ActivityItemProps {
  studentId: string;
  activity: Activity;
}
export default function ActivityItem({
  studentId,
  activity,
}: ActivityItemProps) {
  const [assessment, setAssessment] = useState<Assessment | null>(null);

  const description = useMemo(() => {
    if (!activity) return '';
    let activityDescription = mapActivityToSkill(activity.activityName).Skill;
    if (activity.version === 2) {
      return getActivityContext(activity.activityName, activity.level);
    } else if ('context' in activity) {
      return activity.context;
    }
    return activityDescription;
  }, [activity]);

  const placementLevel = useMemo(() => {
    if (!assessment) return null;
    const placementLevel = assessment.students[studentId]?.placementLevel;
    return placementLevel ?? null;
  }, [studentId, assessment]);

  const controlTypeIcons: ReactNode | null = useMemo(() => {
    const withTooltip = (node: ReactNode, tip: string) => (
      <Tooltip key={tip}>
        <TooltipTrigger>{node}</TooltipTrigger>
        <TooltipContent>{tip}</TooltipContent>
      </Tooltip>
    );
    if ('controlTypesUsed' in activity) {
      const icons = activity.controlTypesUsed
        .map((control) => {
          switch (control) {
            case ControllerType.CLICK:
              return withTooltip(
                <MouseLeftClickIcon size={16} />,
                'Click to Place',
              );
            case ControllerType.DRAG:
              return withTooltip(
                <HandGrabbingIcon size={16} />,
                'Drag and Drop',
              );
            case ControllerType.KEYBOARD:
              return withTooltip(<KeyboardIcon size={16} />, 'Typing');
            default:
              return null;
          }
        })
        .filter((i) => i !== null);
      return icons.length ? (
        <div className="flex items-center">{icons}</div>
      ) : null;
    } else return null;
  }, [activity]);

  useEffect(() => {
    (async () => {
      if (!activity || !activity.assessmentId) return;
      const service = api.asShareLink ?? api.asEducator;
      const assessment = await service.getAssessment(activity.assessmentId);
      if (assessment) setAssessment(assessment);
    })();
  }, [activity]);

  return (
    <>
      <div className="flex flex-col">
        <div className="text-xs text-gray-400 mb-auto">
          {dayjs(activity.startedAt).format('DD/MM/YYYY h:mma')}
        </div>
        <div className="text-sm font-medium">
          {mapActivityToSkill(activity.activityName).CleanName}
        </div>
        <div className="text-xs text-muted-foreground">{description}</div>
      </div>

      {activity.endedAt ? (
        <div className="grid grid-cols-4 text-xs text-muted-foreground gap-4">
          {!placementLevel && <div />}
          {controlTypeIcons ? (
            <div className="flex flex-col justify-center items-center gap-1">
              <span>Control</span>
              <div className="flex items-center">{controlTypeIcons}</div>
            </div>
          ) : (
            <div />
          )}
          {placementLevel && (
            <div className="flex flex-col justify-center items-center gap-1">
              <span>Placement</span>
              <span className="text-sm text-foreground">
                Level {placementLevel}
              </span>
            </div>
          )}
          <div className="flex flex-col justify-center items-center gap-1">
            <span>Accuracy</span>
            <span className="text-sm text-foreground">
              {Math.round(activity.accuracy)}%
            </span>
          </div>
          <div className="flex flex-col justify-center items-center gap-1">
            <span>Time Taken</span>
            <span className="text-sm text-foreground">
              {formatTimeTaken(activity.startedAt, activity.endedAt)}
            </span>
          </div>
        </div>
      ) : (
        <Badge variant="secondary" className="h-fit">
          Not Completed
        </Badge>
      )}
    </>
  );
}
